import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const CustomReportBuilder = ({ onSave, onCancel }) => {
  const [reportConfig, setReportConfig] = useState({
    name: '',
    description: '',
    category: 'operational',
    fields: [],
    groupBy: '',
    sortBy: '',
    filters: {},
    format: 'pdf',
    schedule: 'manual'
  });

  const [selectedFields, setSelectedFields] = useState([]);

  const categoryOptions = [
    { value: 'operational', label: 'Operacional' },
    { value: 'financial', label: 'Financeiro' },
    { value: 'guest', label: 'Hóspedes' },
    { value: 'marketing', label: 'Marketing' }
  ];

  const availableFields = [
    { id: 'occupancy_rate', label: 'Taxa de Ocupação', category: 'operational' },
    { id: 'adr', label: 'Diária Média (ADR)', category: 'financial' },
    { id: 'revpar', label: 'RevPAR', category: 'financial' },
    { id: 'total_revenue', label: 'Receita Total', category: 'financial' },
    { id: 'guest_satisfaction', label: 'Satisfação do Hóspede', category: 'guest' },
    { id: 'booking_source', label: 'Fonte da Reserva', category: 'marketing' },
    { id: 'room_nights', label: 'Diárias Vendidas', category: 'operational' },
    { id: 'cancellation_rate', label: 'Taxa de Cancelamento', category: 'operational' },
    { id: 'no_show_rate', label: 'Taxa de No-Show', category: 'operational' },
    { id: 'average_stay', label: 'Permanência Média', category: 'guest' }
  ];

  const groupByOptions = [
    { value: 'date', label: 'Data' },
    { value: 'hotel', label: 'Hotel' },
    { value: 'room_type', label: 'Tipo de Quarto' },
    { value: 'booking_source', label: 'Fonte da Reserva' },
    { value: 'guest_segment', label: 'Segmento de Hóspede' }
  ];

  const sortByOptions = [
    { value: 'date_asc', label: 'Data (Crescente)' },
    { value: 'date_desc', label: 'Data (Decrescente)' },
    { value: 'value_asc', label: 'Valor (Crescente)' },
    { value: 'value_desc', label: 'Valor (Decrescente)' }
  ];

  const formatOptions = [
    { value: 'pdf', label: 'PDF' },
    { value: 'excel', label: 'Excel' },
    { value: 'csv', label: 'CSV' }
  ];

  const scheduleOptions = [
    { value: 'manual', label: 'Manual' },
    { value: 'daily', label: 'Diário' },
    { value: 'weekly', label: 'Semanal' },
    { value: 'monthly', label: 'Mensal' }
  ];

  const filteredFields = availableFields.filter(field => 
    reportConfig.category === 'all' || field.category === reportConfig.category
  );

  const handleFieldToggle = (fieldId) => {
    setSelectedFields(prev => 
      prev.includes(fieldId) 
        ? prev.filter(id => id !== fieldId)
        : [...prev, fieldId]
    );
  };

  const handleSave = () => {
    const finalConfig = {
      ...reportConfig,
      fields: selectedFields
    };
    onSave(finalConfig);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Icon name="Settings" size={20} color="var(--color-primary)" />
          <h3 className="text-lg font-semibold text-foreground">Construtor de Relatório Personalizado</h3>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          <Button variant="default" onClick={handleSave} iconName="Save" iconPosition="left">
            Salvar Relatório
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Basic Configuration */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-foreground">Configuração Básica</h4>
          
          <Input
            label="Nome do Relatório"
            placeholder="Ex: Relatório de Ocupação Mensal"
            value={reportConfig.name}
            onChange={(e) => setReportConfig(prev => ({ ...prev, name: e.target.value }))}
          />

          <Input
            label="Descrição"
            placeholder="Descreva o propósito do relatório"
            value={reportConfig.description}
            onChange={(e) => setReportConfig(prev => ({ ...prev, description: e.target.value }))}
          />

          <Select
            label="Categoria"
            options={categoryOptions}
            value={reportConfig.category}
            onChange={(value) => setReportConfig(prev => ({ ...prev, category: value }))}
          />

          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Formato de Saída"
              options={formatOptions}
              value={reportConfig.format}
              onChange={(value) => setReportConfig(prev => ({ ...prev, format: value }))}
            />

            <Select
              label="Agendamento"
              options={scheduleOptions}
              value={reportConfig.schedule}
              onChange={(value) => setReportConfig(prev => ({ ...prev, schedule: value }))}
            />
          </div>
        </div>

        {/* Field Selection */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-foreground">Campos do Relatório</h4>
          
          <div className="max-h-64 overflow-y-auto border border-border rounded-lg p-4 space-y-2">
            {filteredFields.map((field) => (
              <div key={field.id} className="flex items-center space-x-3">
                <Checkbox
                  checked={selectedFields.includes(field.id)}
                  onChange={() => handleFieldToggle(field.id)}
                />
                <label className="text-sm text-foreground cursor-pointer flex-1">
                  {field.label}
                </label>
              </div>
            ))}
          </div>

          <div className="text-xs text-muted-foreground">
            {selectedFields.length} campos selecionados
          </div>
        </div>
      </div>

      {/* Advanced Options */}
      <div className="mt-6 pt-6 border-t border-border">
        <h4 className="text-sm font-semibold text-foreground mb-4">Opções Avançadas</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Agrupar Por"
            options={groupByOptions}
            value={reportConfig.groupBy}
            onChange={(value) => setReportConfig(prev => ({ ...prev, groupBy: value }))}
            placeholder="Selecione um agrupamento"
          />

          <Select
            label="Ordenar Por"
            options={sortByOptions}
            value={reportConfig.sortBy}
            onChange={(value) => setReportConfig(prev => ({ ...prev, sortBy: value }))}
            placeholder="Selecione uma ordenação"
          />
        </div>
      </div>

      {/* Preview */}
      <div className="mt-6 pt-6 border-t border-border">
        <h4 className="text-sm font-semibold text-foreground mb-4">Pré-visualização</h4>
        
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="FileText" size={16} color="var(--color-primary)" />
            <span className="text-sm font-medium text-foreground">
              {reportConfig.name || 'Relatório Sem Nome'}
            </span>
          </div>
          
          <p className="text-xs text-muted-foreground mb-3">
            {reportConfig.description || 'Sem descrição'}
          </p>
          
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div>
              <span className="text-muted-foreground">Categoria:</span>
              <span className="ml-2 text-foreground">
                {categoryOptions.find(c => c.value === reportConfig.category)?.label}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Formato:</span>
              <span className="ml-2 text-foreground">
                {formatOptions.find(f => f.value === reportConfig.format)?.label}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Campos:</span>
              <span className="ml-2 text-foreground">{selectedFields.length}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Agendamento:</span>
              <span className="ml-2 text-foreground">
                {scheduleOptions.find(s => s.value === reportConfig.schedule)?.label}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomReportBuilder;